import {
  colorSwatches as routeColorSwatches,
  colors,
} from "../../Maps/ActiveRoutes/colors";
import {
  normalizeRouteColor,
  resolveRouteColorPalette,
} from "../../Maps/ActiveRoutes/routeColor";
import type { RouteColorName } from "../../../types/routeBuilder";

/** @deprecated Use `routeColorSwatches` from ActiveRoutes/colors instead. */
export const colorSwatches = routeColorSwatches;

export const COLOR_RED = { name: "red", hex: colors.red.hex };
export const COLOR_BLUE = { name: "blue", hex: colors.blue.hex };
export const COLOR_BLACK = { name: "black", hex: colors.black.hex };

export function normalizeSiteMapColor(
  color: string | null | undefined,
  fallback: RouteColorName = "red",
): RouteColorName {
  if (color && Object.prototype.hasOwnProperty.call(colors, color)) {
    return color as RouteColorName;
  }
  if (!color) return fallback;
  return normalizeRouteColor(color);
}

export function siteMapColorHex(
  color: string | null | undefined,
  fallback: RouteColorName = "red",
): string {
  return resolveRouteColorPalette(normalizeSiteMapColor(color, fallback)).hex;
}

/**
 * Lightens a hex color by a given percentage
 */
export function lightenColor(color: string, percentage: number): string {
  // Remove # if present
  const hex = color.replace("#", "");

  // Parse RGB components
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Lighten by percentage
  const factor = percentage / 100;
  const newR = Math.round(r + (255 - r) * factor);
  const newG = Math.round(g + (255 - g) * factor);
  const newB = Math.round(b + (255 - b) * factor);

  // Convert back to hex
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}
/**
 * Darkens a hex color by a given percentage
 */
export function darkenColor(color: string, percentage: number): string {
  // Remove # if present
  const hex = color.replace("#", "");

  // Parse RGB components
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Darken by percentage
  const factor = percentage / 100;
  const newR = Math.round(Math.max(0, r * (1 - factor)));
  const newG = Math.round(Math.max(0, g * (1 - factor)));
  const newB = Math.round(Math.max(0, b * (1 - factor)));

  // Convert back to hex
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}

export function applySaturationToHexColor(
  hex: string,
  saturationPercent: number,
) {
  if (!/^#([0-9a-f]{6})$/i.test(hex)) {
    console.warn("Unexpected color format: " + hex);
    return hex;
  }

  if (saturationPercent < 0 || saturationPercent > 100) {
    throw "Unexpected saturation value";
  }

  const saturationFloat = saturationPercent / 100,
    rgbIntensityFloat = [
      parseInt(hex.substr(1, 2), 16) / 255,
      parseInt(hex.substr(3, 2), 16) / 255,
      parseInt(hex.substr(5, 2), 16) / 255,
    ];

  const rgbIntensityFloatSorted = rgbIntensityFloat
      .slice(0)
      .sort(function (a, b) {
        return a - b;
      }),
    maxIntensityFloat = rgbIntensityFloatSorted[2],
    mediumIntensityFloat = rgbIntensityFloatSorted[1],
    minIntensityFloat = rgbIntensityFloatSorted[0];

  if (maxIntensityFloat == minIntensityFloat) {
    // All colors have same intensity, which means
    // the original color is gray, so we can't change saturation.
    return hex;
  }

  // New color max intensity wont change. Lets find medium and weak intensities.

  var newMediumIntensityFloat,
    newMinIntensityFloat = maxIntensityFloat * (1 - saturationFloat);

  if (mediumIntensityFloat == minIntensityFloat) {
    // Weak colors have equal intensity.
    newMediumIntensityFloat = newMinIntensityFloat;
  } else {
    // Calculate medium intensity with respect to original intensity proportion.
    const intensityProportion =
      (maxIntensityFloat - mediumIntensityFloat) /
      (mediumIntensityFloat - minIntensityFloat);
    newMediumIntensityFloat =
      (intensityProportion * newMinIntensityFloat + maxIntensityFloat) /
      (intensityProportion + 1);
  }

  var newRgbIntensityFloat: number[] = [],
    newRgbIntensityFloatSorted = [
      newMinIntensityFloat,
      newMediumIntensityFloat,
      maxIntensityFloat,
    ];

  // We've found new intensities, but we have then sorted from min to max.
  // Now we have to restore original order.
  rgbIntensityFloat.forEach(function (originalRgb) {
    var rgbSortedIndex = rgbIntensityFloatSorted.indexOf(originalRgb);
    newRgbIntensityFloat.push(newRgbIntensityFloatSorted[rgbSortedIndex]);
  });

  var floatToHex = function (val: number) {
      return ("0" + Math.round(val * 255).toString(16)).substr(-2);
    },
    rgb2hex = function (rgb: number[]) {
      return "#" + floatToHex(rgb[0]) + floatToHex(rgb[1]) + floatToHex(rgb[2]);
    };

  var newHex = rgb2hex(newRgbIntensityFloat);

  return newHex;
}
